import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ImageUrlPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { DEFAULT_SHOWS_DEFINITION, Show, ShowsDefinition } from '../shows.model';
import { ShowsService } from '../shows.service';

/**
 * Every published show, in the order an editor gave them: `/shows`.
 */
@Component({
  selector: 'multisite-shows-index',
  imports: [RouterModule, TranslocoModule, ImageUrlPipe, LoaderGlobeAnimationDetailComponent],
  providers: [provideTranslocoScope('shows')],
  template: `
    <div class="container my-4" *transloco="let t">
      <h1 class="font-weight-bold mb-4">{{ t('navigation.shows') }}</h1>

      @if (loading() && !shows().length) {
        <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
      }

      @if (failed()) {
        <p class="text-danger">{{ t('error.could_not_load') }}</p>
      }

      <div class="row">
        @for (show of shows(); track show.id) {
          <div class="col-md-6 col-lg-4 mb-4">
            <a class="card h-100 text-reset text-decoration-none" [routerLink]="[definition.showsPath, show.slug]">
              @if (artwork(show); as image) {
                <img class="card-img-top" [src]="image | imageUrl: 'w_400,c_fill,ar_1:1'" [alt]="image.alt_text ?? ''" />
              } @else {
                <div class="card-img-top" style="aspect-ratio: 1" [style.background-color]="show.colours?.primary ?? '#e9ecef'"></div>
              }
              <div class="card-body">
                <p class="h5 card-title font-weight-bold">{{ show.name }}</p>
                @if (show.tagline) {
                  <p class="card-text text-muted">{{ show.tagline }}</p>
                }
              </div>
            </a>
          </div>
        } @empty {
          @if (!loading() && !failed()) {
            <p>{{ t('common.no_results') }}</p>
          }
        }
      </div>
    </div>
  `,
})
export class ShowsIndexComponent {
  private showsService = inject(ShowsService);
  readonly definition: ShowsDefinition = inject(ActivatedRoute).snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;

  shows = signal<Show[]>([]);
  loading = signal(false);
  failed = signal(false);

  constructor() {
    this.load();
    reloadOnLocaleChange(() => this.load());
  }

  artwork(show: Show) {
    return show.images.find((image) => image.role === 'logo_square') ?? null;
  }

  private load(): void {
    this.loading.set(true);
    this.failed.set(false);
    this.showsService
      .getShows({ pageSize: 100, include: ['images'] })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({ next: (page) => this.shows.set(page.items), error: () => this.failed.set(true) });
  }
}
